<template>
  <div class="recruiter-pitch">
    <div v-if="errorBannerMessage" class="recruiter-pitch-error m-b-15">
      {{ errorBannerMessage }}
    </div>
    <WdsAlert v-if="hasSubmissionLimit" info class="tw-mb-3" data-test="submission-limit-alert">
      {{
        $t(
          ':company has restricted the number of candidate submissions on this hire. The total number of candidate submissions allowed is :limit.',
          { company: submissionLimitSetBy, limit: maxCandidateSubmissions },
        )
      }}
    </WdsAlert>
    <BaseButton
      size="sm"
      class="add-candidate-button"
      :disabled="submissionLimitReached"
      @click="!submissionLimitReached && setShowCandidateSelect(!showCandidateSelect)"
    >
      <WdsStack align-center gap-sm data-test="add-candidate-button">
        <i class="zmdi zmdi-account-add" />
        {{ $t('Add a candidate') }}
      </WdsStack>
    </BaseButton>
    <CandidateSelect
      v-click-outside="onClickOutside"
      :show="showCandidateSelect"
      class="candidate-select"
      :candidates="filteredFreelancers"
      :show-create-button="canCreateCandidates"
      :search-term="searchTerm"
      :loading="$apollo.queries.candidates.loading"
      :on-click-create="() => toggleShowRecruiterFreelancerModal(true)"
      @add-candidate="addCandidate"
      @input="updateSearchTerm"
    />
    <RecruiterFreelancerModal
      :show="showRecruiterFreelancerModal"
      :on-cancel="() => toggleShowRecruiterFreelancerModal(false)"
      :on-confirm="onConfirmAddFreelancer"
      :currency="currency"
      :title="$t('Create candidate')"
      :help-text="
        !managesWorkers
          ? $t('The candidate will not be contacted before they have been selected for a job.')
          : null
      "
      :manages-workers="managesWorkers"
      :rate-required="rateRequired"
      :error-text="$t('An error occurred while trying to invite candidate')"
      :save-button-text="$t('Add')"
      :save-button-text-while-working="$t('Adding')"
    />
    <WdsAlert v-if="formErrors['input.candidates']" error class="tw-mt-2">
      {{ formErrors['input.candidates'][0] }}
    </WdsAlert>
    <WdsStack v-show="$apollo.queries.pitchedCandidates.loading" justify-center>
      <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
      <button-spinner :size="sizeNames.LG"></button-spinner>
    </WdsStack>
    <WdsStack align-start justify-space-between gap-custom="0px 16px" wrap>
      <PitchFreelancerCard
        v-for="(selectedCandidate, idx) in selectedCandidates"
        :key="selectedCandidate.freelancer.globalId"
        v-model:candidate="selectedCandidates[idx]"
        :avatar-path="
          selectedCandidate.freelancer.hasPicture ? selectedCandidate.freelancer.picture : undefined
        "
        :global-id="selectedCandidate.freelancer.globalId"
        :initial-comment="selectedCandidate.comment"
        :name="selectedCandidate.freelancer.name"
        :email="selectedCandidate.freelancer.email || selectedCandidate.inviteEmail"
        :initials="selectedCandidate.freelancer.initials"
        :currency="currency"
        :errors="{
          rate: formErrors[`input.candidates.${idx}.rate`],
          rateType: formErrors[`input.candidates.${idx}.rateType`],
          cvFiles: formErrors[`input.candidates.${idx}.cvFiles`],
          comment: formErrors[`input.candidates.${idx}.brief`],
          workerType: formErrors[`input.candidates.${idx}.workerType`],
          ...getLinksErrors(formErrors, idx),
        }"
        :profile-url="selectedCandidate.freelancer.primaryProfile?.url"
        :stage="selectedCandidate.stage ?? ''"
        :rate-required="rateRequired"
        :mixed-mode-allowed="mixedModeAllowed"
        :manages-workers="managesWorkers"
        @remove-freelancer="removeCandidate(idx, selectedCandidate.freelancer.globalId)"
      />
    </WdsStack>
    <template v-if="contactDetailsRequired">
      <div class="tw-mt-4 tw-mb-2 f-500">
        {{ $t('Your contact details') }}
      </div>
      <ContactDetailsFields
        v-model:name="contactName"
        v-model:email="contactEmail"
        v-model:phone="contactPhone"
        :required="true"
        data-test="submission-contact-details"
      />
    </template>
    <div class="tw-mt-4 tw-mb-2 f-500">
      {{ $t('Write a message with your submission') }}
    </div>
    <WdsInputArea
      v-model="pitchMessage"
      name="recruiter-pitch"
      :placeholder="
        $t(
          'Enter e.g. a bit about your candidates and their qualifications and why they would be a good fit',
        )
      "
    />
    <div class="recruiter-pitch-footer">
      <WdsStack justify-space-between align-center>
        <div class="terms-banner c-gray-700">
          <div class="info-icon-wrapper">
            <i class="fa fa-info" />
          </div>
          {{ $t('When you submit, you also accept the') }}
          <a href="/pages/terms" class="c-gray-700" target="_blank">
            {{ `${$t('Worksome terms and conditions')}.` }}
          </a>
        </div>
        <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
        <ButtonWithSpinner
          color="primary"
          size="sm"
          :status="buttonStatus"
          :disabled="submitButtonDisabled"
          data-test="submit-candidates"
          @click="sendPitch"
        >
          {{ isEdit ? $t('Save submission') : $t('Submit') }}
        </ButtonWithSpinner>
      </WdsStack>
    </div>
  </div>
</template>

<script>
import gql from 'graphql-tag'
import { WdsStack, WdsInputArea, WdsAlert } from '@worksome/wds-components'
import isEqual from 'lodash/isEqual'
import { deepClone } from '@/utils/tools'
import BaseButton from '@/components/BaseButton.vue'
import PitchFreelancerCard from '@/components/PitchFreelancerCard.vue'
import ContactDetailsFields from '@/components/ContactDetails/ContactDetailsFields.vue'
import CandidateSelect from '@/components/CandidateSelect.vue'
import { FormValidation } from '@/utils/formValidation'
import enums, { SizeNames } from '@/utils/enum'
import RecruiterFreelancerModal from '@/components/Contacts/RecruiterFreelancer/RecruiterFreelancerModal.vue'
import { AvailableCandidates } from '@/components/Contacts/RecruiterFreelancer/oldGraphql/queries'
import { QuickCreateRecruiterFreelancer } from '@/components/Contacts/RecruiterFreelancer/oldGraphql/mutations'
import { FeeApplication, FeeBasis, MarketCode, RateType } from '@/gql/graphql'
import { PITCH_FREELANCER_FRAGMENT } from '@/components/Contacts/RecruiterFreelancer/oldGraphql/fragments'
import { user } from '@/composables/useUser'

/**
 * Maps the offer's rate type to the matching duration FeeBasis. The supplier
 * referral fee must be stored with — and looked up by — the basis that
 * corresponds to the offer's per-unit rate, otherwise downstream consumers
 * (PresentToClientModal, PresentCandidateToClient) silently miss the fee for
 * non-hourly offers. FIXED/UNKNOWN have no duration basis, so we omit them
 * here and skip sending/reading a fee in those flows.
 */
const FEE_BASIS_BY_RATE_TYPE = {
  [RateType.Hourly]: FeeBasis.Hourly,
  [RateType.Daily]: FeeBasis.Daily,
  [RateType.Weekly]: FeeBasis.Weekly,
  [RateType.Monthly]: FeeBasis.Monthly,
}

const AvailableSupplierCandidates = gql`
  query supplierCandidates($rateType: RateType!, $currency: String!) {
    me {
      activeAccount {
        globalId
        ... on Company {
          supplierCandidates {
            data {
              localId
              globalId
              inviteEmail
              status
              currency
              hourRate
              dailyRate
              monthlyRate
              jobTitle
              links
              rate(rateType: $rateType, currency: $currency)
              files {
                globalId
                localId
                name
              }
              createdAt
              freelancer {
                ...PitchFreelancerFragment
              }
            }
          }
        }
      }
    }
  }
  ${PITCH_FREELANCER_FRAGMENT}
`

const CreateSupplierCandidateMutation = gql`
  mutation createSupplierCandidate(
    $firstName: String!
    $middleName: String
    $lastName: String!
    $email: String!
    $jobTitle: String
    $country: String
    $state: String
    $currency: String!
    $hourlyRate: Float
    $dailyRate: Float
    $monthlyRate: Float
    $tags: [String!]
    $links: [String!]
    $files: [ID!]
    $rateType: RateType!
  ) {
    createSupplierCandidate(
      input: {
        firstName: $firstName
        middleName: $middleName
        lastName: $lastName
        email: $email
        jobTitle: $jobTitle
        country: $country
        state: $state
        currency: $currency
        hourlyRate: $hourlyRate
        dailyRate: $dailyRate
        monthlyRate: $monthlyRate
        tags: $tags
        links: $links
        files: $files
      }
    ) {
      localId
      globalId
      inviteEmail
      status
      currency
      hourRate
      dailyRate
      monthlyRate
      jobTitle
      links
      rate(rateType: $rateType, currency: $currency)
      files {
        globalId
        localId
        name
      }
      createdAt
      freelancer {
        ...PitchFreelancerFragment
      }
    }
  }
  ${PITCH_FREELANCER_FRAGMENT}
`

export default {
  components: {
    BaseButton,
    WdsStack,
    WdsInputArea,
    WdsAlert,
    PitchFreelancerCard,
    CandidateSelect,
    RecruiterFreelancerModal,
    ContactDetailsFields,
  },
  props: {
    /**
     * Job id that recruiter is pitching candidates for
     */
    jobId: {
      type: Number,
      required: true,
    },
    /**
     * Supplier id / id of recruiter
     */
    supplierId: {
      type: String,
      required: true,
    },
    /**
     * Account type: 'recruiter' or 'company'
     */
    accountType: {
      type: String,
      default: 'recruiter',
    },
    /**
     * Rate type of job posting (ex. Rate per hour or Rate per day)
     */
    rateType: {
      type: String,
      required: true,
    },
    /**
     * Rate type name
     */
    rateTypeName: {
      type: String,
      required: true,
    },
    /**
     * Currency of job posting
     * */
    currency: {
      type: String,
      required: true,
    },
    /**
     * Initial message recruiter sent with pitch. This is only relevant in an edit flow.
     * */
    initialMessage: {
      type: String,
      default: '',
    },
    /**
     * Redirect route on successful pitch.
     * */
    redirectRoute: {
      type: String,
      default: '',
    },
    /**
     * If the managed workers setup is being used.
     * */
    managesWorkers: {
      type: Boolean,
      default: false,
    },
    /**
     * Whether the source company's organisation allows mixing managed/referred workers.
     * When false the worker type can't be picked per submission and falls back to
     * the supplier's manages-workers configuration.
     * */
    mixedModeAllowed: {
      type: Boolean,
      default: false,
    },
    isSubmittingToMsp: {
      type: Boolean,
      default: false,
    },
    contactPrefillName: {
      type: String,
      default: '',
    },
    contactPrefillEmail: {
      type: String,
      default: '',
    },
    contactPrefillPhone: {
      type: String,
      default: '',
    },
    /**
     * Maximum candidates this supplier may submit on the job; null for no limit.
     * */
    maxCandidateSubmissions: {
      type: Number,
      default: null,
    },
    /**
     * Name of the client that set the submission limit.
     * */
    submissionLimitSetBy: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      errorBannerMessage: '',
      pitchMessage: this.initialMessage,
      contactName: this.contactPrefillName ?? '',
      contactEmail: this.contactPrefillEmail ?? '',
      contactPhone: this.contactPrefillPhone ?? '',
      selectedCandidates: [],
      formErrors: {},
      showCandidateSelect: false,
      searchTerm: '',
      candidates: [],
      buttonStatus: 'enabled',
      showRecruiterFreelancerModal: false,
      sizeNames: SizeNames,
      initialSelectedCandidates: [],
      candidatesUpdated: false,
      messageUpdated: false,
    }
  },
  computed: {
    canCreateCandidates() {
      return user.value.hasPermission('manage-candidates')
    },
    hasSubmissionLimit() {
      return typeof this.maxCandidateSubmissions === 'number' && this.maxCandidateSubmissions > 0
    },
    submissionLimitReached() {
      return this.hasSubmissionLimit && this.selectedCandidates.length >= this.maxCandidateSubmissions
    },
    rateRequired() {
      return currentAccount.market?.toUpperCase() === MarketCode.Nl
    },
    isCompanySupplier() {
      return this.accountType === 'company'
    },
    // Only display candidates in the dropdown who (1) have not already been included
    // in the pitch and (2) match the current search term
    filteredFreelancers() {
      return this.candidates
        .filter(
          (recruiterFreelancer) =>
            this.freelancerIsAvailable(recruiterFreelancer) &&
            this.freelancerMatchesSearchTerm(recruiterFreelancer),
        )
        .sort(
          (recruiterFreelancerA, recruiterFreelancerB) =>
            new Date(recruiterFreelancerB.createdAt).getTime() -
            new Date(recruiterFreelancerA.createdAt).getTime(),
        )
    },
    contactDetailsRequired() {
      return this.isSubmittingToMsp
    },
    contactDetailsComplete() {
      return (
        !!this.contactName.trim() && !!this.contactEmail.trim() && !!this.contactPhone.trim()
      )
    },
    submitButtonDisabled() {
      if (this.$apollo.queries.pitchedCandidates.loading) {
        return true
      }

      if (this.buttonStatus === 'working') {
        return true
      }

      if (this.contactDetailsRequired && !this.contactDetailsComplete) {
        return true
      }

      if (this.isEdit) {
        return (
          this.selectedCandidates.length === 0 || !(this.candidatesUpdated || this.messageUpdated)
        )
      }

      return false
    },
    isEdit() {
      return this.initialSelectedCandidates.length > 0
    },
  },
  apollo: {
    candidates: {
      query() {
        return this.isCompanySupplier ? AvailableSupplierCandidates : AvailableCandidates
      },
      variables() {
        return {
          rateType: this.rateType,
          currency: this.currency,
        }
      },
      update(data) {
        if (this.isCompanySupplier) {
          return data?.me?.activeAccount?.supplierCandidates?.data ?? []
        }
        return data?.me?.activeAccount?.candidates?.data ?? []
      },
    },
    pitchedCandidates: {
      query: gql`
        query pitchedCandidates(
          $jobId: Int!
          $recruiterId: ID!
          $rateType: RateType!
          $currency: String!
        ) {
          me {
            activeAccount {
              globalId
              ... on Recruiter {
                pitchedOffers(yjobId: $jobId, status: [OPEN, ACCEPTED]) {
                  data {
                    globalId
                    localId
                    rate
                    ratetype
                    brief
                    links
                    availableFrom
                    workerType
                    candidateStep
                    fees {
                      globalId
                      feeType
                      application
                      basis
                      rate
                    }
                    files {
                      name
                      globalId
                      localId
                      createdAt
                      tags {
                        name
                      }
                    }
                    freelancer {
                      localId
                      globalId
                      name
                      picture
                      hasPicture
                      initials
                      primaryProfile {
                        url
                      }
                      candidates(filter: { recruiterId: $recruiterId }, first: 1) {
                        data {
                          rate(rateType: $rateType, currency: $currency)
                          inviteEmail
                          hourRate
                          dailyRate
                          monthlyRate
                        }
                      }
                    }
                  }
                }
              }
              ... on Company {
                pitchedOffers(yjobId: $jobId, status: [OPEN, ACCEPTED]) {
                  data {
                    globalId
                    localId
                    rate
                    ratetype
                    brief
                    links
                    availableFrom
                    workerType
                    candidateStep
                    fees {
                      globalId
                      feeType
                      application
                      basis
                      rate
                    }
                    files {
                      name
                      globalId
                      localId
                      createdAt
                    }
                    freelancer {
                      localId
                      globalId
                      name
                      picture
                      hasPicture
                      initials
                      primaryProfile {
                        url
                      }
                    }
                  }
                }
              }
            }
          }
        }
      `,
      variables() {
        return {
          jobId: this.jobId,
          recruiterId: this.supplierId,
          rateType: this.rateType,
          currency: this.currency,
        }
      },
      update(data) {
        const pitchedOffers = data.me.activeAccount.pitchedOffers?.data ?? []
        pitchedOffers
          .map((offer) => {
            // The supplier pitch flow stores a single supplier referral fee per
            // offer, with a basis matching the offer's rate type. Look it up
            // by the same derived basis so re-pitching a non-hourly offer
            // pre-populates the existing value instead of silently resetting.
            const offerRateType = offer.ratetype.toUpperCase()
            const expectedReferralBasis = FEE_BASIS_BY_RATE_TYPE[offerRateType]
            const supplierReferralFee = expectedReferralBasis
              ? offer.fees?.find(
                  (fee) =>
                    fee.feeType === 'REFERRAL'
                    && fee.basis === expectedReferralBasis
                    && fee.application === FeeApplication.Additive,
                )
              : null

            return {
              offerId: offer.localId,
              globalId: offer.globalId,
              rate: offer.rate,
              comment: offer.brief,
              freelancer: {
                ...offer.freelancer,
                rate: offer.freelancer.candidates?.data[0]?.rate,
                email: offer.freelancer.candidates?.data[0]?.inviteEmail,
              },
              links: offer.links?.map((link, index) => ({ key: index, url: link })) ?? [],
              files: [...offer.files]
                .filter((file) => !file.tags?.some((tag) => tag.name === 'CV'))
                .sort((file1, file2) => new Date(file2.createdAt) - new Date(file1.createdAt)),
              cvFiles: [...offer.files]
                .filter((file) => file.tags?.some((tag) => tag.name === 'CV'))
                .sort((file1, file2) => new Date(file2.createdAt) - new Date(file1.createdAt)),
              availableFrom: offer.availableFrom,
              rateType: offer.ratetype.toUpperCase(),
              hourRate: offer.freelancer.candidates?.data[0]?.hourRate,
              dailyRate: offer.freelancer.candidates?.data[0]?.dailyRate,
              monthlyRate: offer.freelancer.candidates?.data[0]?.monthlyRate,
              workerType: offer.workerType ?? null,
              referralFee: supplierReferralFee ? Number(supplierReferralFee.rate) : null,
              stage: offer.candidateStep ?? '',
            }
          })
          .forEach((offer) => {
            this.selectedCandidates.push(offer)
            this.initialSelectedCandidates.push(offer)
          })
      },
    },
  },
  watch: {
    pitchMessage() {
      this.messageUpdated = this.pitchMessage.length > 0
    },
    selectedCandidates: {
      handler() {
        if (this.isEdit) {
          // If there are no candidates, we can't really proceed
          if (this.selectedCandidates.length === 0) {
            this.candidatesUpdated = false

            return
          }

          if (this.selectedCandidates.length !== this.initialSelectedCandidates.length) {
            this.candidatesUpdated = true

            return
          }

          let hasChanged = false

          // Determine if the candidate list has changed
          this.initialSelectedCandidates.every((initialSelectedCandidate) => {
            const matchingCandidate = this.selectedCandidates.find(
              (candidate) =>
                candidate.freelancer.globalId === initialSelectedCandidate.freelancer.globalId,
            )

            let candidateChanged = false

            if (matchingCandidate) {
              candidateChanged = !isEqual(initialSelectedCandidate, matchingCandidate)
            } else {
              // Most likely a new candidate
              candidateChanged = true
            }

            if (hasChanged === false && candidateChanged) {
              hasChanged = true
            }

            return true
          })

          this.candidatesUpdated = hasChanged
        }
      },
      deep: true,
    },
  },
  methods: {
    toggleShowRecruiterFreelancerModal(value) {
      this.showRecruiterFreelancerModal = value
    },
    freelancerIsAvailable(recruiterFreelancer) {
      return !this.selectedCandidates.find(
        (selectedCandidate) =>
          selectedCandidate.freelancer.globalId === recruiterFreelancer.freelancer.globalId,
      )
    },
    freelancerMatchesSearchTerm(recruiterFreelancer) {
      return (
        recruiterFreelancer.inviteEmail.toLowerCase().includes(this.searchTerm) ||
        recruiterFreelancer.freelancer.name.toLowerCase().includes(this.searchTerm)
      )
    },
    addCandidate(candidate) {
      const allFiles = candidate?.files ?? []
      this.selectedCandidates = [
        ...this.selectedCandidates,
        {
          ...candidate,
          rateType: this.rateType,
          files: allFiles.filter((file) => !file.tags?.some((tag) => tag.name === 'CV')),
          cvFiles: allFiles.filter((file) => file.tags?.some((tag) => tag.name === 'CV')),
          links: candidate?.links.map((link, index) => ({ key: index, url: link })) ?? [],
        },
      ]
      this.showCandidateSelect = false
      this.searchTerm = ''
      this.formErrors['input.candidates'] = undefined
    },
    removeCandidate(candidateIndex, globalId) {
      const {
        [`input.candidates.${candidateIndex}.rate`]: _rate,
        [`input.candidates.${candidateIndex}.rateType`]: _rateType,
        [`input.candidates.${candidateIndex}.cvFiles`]: _cvFiles,
        [`input.candidates.${candidateIndex}.brief`]: _brief,
        [`input.candidates.${candidateIndex}.workerType`]: _workerType,
        ...remainingErrors
      } = this.formErrors

      // Clear form errors for removed candidates
      this.formErrors = remainingErrors

      this.selectedCandidates = this.selectedCandidates.filter(
        (selectedCandidate) => selectedCandidate.freelancer.globalId !== globalId,
      )
    },
    async sendPitch() {
      this.formErrors = {}

      if (this.selectedCandidates.length === 0) {
        this.formErrors['input.candidates'] = [
          $t('Add at least one possible candidate for the job'),
        ]
      }

      if (this.rateRequired) {
        this.selectedCandidates.forEach((candidate, idx) => {
          if (!candidate.rate) {
            this.formErrors[`input.candidates.${idx}.rate`] = [
              $t('Rate is required'),
            ]
          }
          if (!candidate.rateType || candidate.rateType === RateType.Unknown) {
            this.formErrors[`input.candidates.${idx}.rateType`] = [
              $t('Rate type is required'),
            ]
          }
          if (!candidate.cvFiles?.length) {
            this.formErrors[`input.candidates.${idx}.cvFiles`] = [
              $t('CV is required'),
            ]
          }
        })
      }

      // When the source company allows mixing managed/referred workers the
      // supplier must explicitly pick a submission type per candidate.
      // Outside of mixed mode the value is auto-filled in PitchFreelancerCard's
      // created() hook based on managesWorkers, so no validation is needed.
      if (this.mixedModeAllowed) {
        this.selectedCandidates.forEach((candidate, idx) => {
          if (!candidate.workerType) {
            this.formErrors[`input.candidates.${idx}.workerType`] = [
              $t('Submission type is required'),
            ]
          }
        })
      }

      if (this.contactDetailsRequired) {
        if (!this.contactName.trim()) {
          this.formErrors['input.contactName'] = [$t('Contact name is required.')]
        }
        if (!this.contactEmail.trim()) {
          this.formErrors['input.contactEmail'] = [$t('Contact email is required.')]
        }
        if (!this.contactPhone.trim()) {
          this.formErrors['input.contactPhone'] = [$t('Contact phone is required.')]
        }
      }

      if (Object.keys(this.formErrors).length > 0) {
        return
      }

      this.buttonStatus = 'working'

      try {
        await this.syncCvFileTags()
      } catch {
        this.buttonStatus = 'enabled'
        this.errorBannerMessage = $t('An error occurred while trying to submit candidates')

        this.$store.commit('sendNotification', {
          type: enums.notification.types.ERROR,
          msg: this.$t('An error occurred while trying to submit candidates'),
          sender: this,
          severity: enums.notification.severity.CRITICAL,
        })

        return
      }

      const input = {
        supplierId: this.supplierId,
        message: this.pitchMessage,
        contactName: this.contactName || null,
        contactEmail: this.contactEmail || null,
        contactPhone: this.contactPhone || null,
        candidates: [...this.selectedCandidates].map((candidate) => ({
          offerId: candidate.offerId || null,
          freelancerId: candidate.freelancer.globalId,
          rate: candidate.rate,
          // Remove duplicate or empty links
          links: candidate.links?.length
            ? [...new Set(candidate.links.filter((link) => link.url).map((link) => link.url))]
            : null,
          files: [...(candidate.files ?? []), ...(candidate.cvFiles ?? [])].length
            ? [...(candidate.files ?? []), ...(candidate.cvFiles ?? [])].map((file) => file.globalId)
            : undefined,
          rateType: candidate.rateType,
          brief: candidate.comment || null,
          availableFrom: candidate.availableFrom,
          fee: candidate.referralFee && FEE_BASIS_BY_RATE_TYPE[candidate.rateType]
            ? {
                rate: candidate.referralFee,
                basis: FEE_BASIS_BY_RATE_TYPE[candidate.rateType],
              }
            : null,
          workerType: candidate.workerType || null,
        })),
      }

      this.$apollo
        .mutate({
          mutation: gql`
            mutation ($yjobId: Int!, $input: PitchFreelancersInput!) {
              pitchFreelancers(yjobId: $yjobId, input: $input) {
                globalId
                localId
              }
            }
          `,
          variables: {
            yjobId: this.jobId,
            input,
          },
        })
        .then(({ data }) => {
          window.location.href = `${this.redirectRoute}/${data.pitchFreelancers.localId}`
        })
        .catch(({ graphQLErrors }) => {
          this.buttonStatus = 'enabled'

          if (FormValidation.containsFormValidations(graphQLErrors)) {
            this.formErrors = FormValidation.fromGraphqlErrors(graphQLErrors)
          } else {
            this.errorBannerMessage = $t('An error occurred while trying to submit candidates')

            this.$store.commit('sendNotification', {
              type: enums.notification.types.ERROR,
              msg: this.$t(`An error occurred while trying to submit candidates`),
              sender: this,
              severity: enums.notification.severity.CRITICAL,
            })
          }
        })
    },
    setShowCandidateSelect(showCandidateSelect) {
      this.showCandidateSelect = showCandidateSelect
    },
    updateSearchTerm(event) {
      this.searchTerm = event.target.value.toLowerCase()
    },
    onClickOutside() {
      if (!this.showRecruiterFreelancerModal) {
        this.setShowCandidateSelect(false)
        this.searchTerm = ''
      }
    },
    async onConfirmAddFreelancer(data) {
      if (this.isCompanySupplier) {
        return this.createSupplierCandidateAndAdd(data)
      }
      return this.createRecruiterCandidateAndAdd(data)
    },
    async createRecruiterCandidateAndAdd(data) {
      const createRecruiterFreelancerMutation = {
        mutation: QuickCreateRecruiterFreelancer,
        variables: {
          firstName: data.firstName,
          middleName: data.middleName,
          lastName: data.lastName,
          email: data.email,
          jobTitle: data.jobTitle,
          country: data.country,
          state: data.state,
          currency: this.currency,
          rateType: this.rateType,
          hourlyRate: data.hourlyRate,
          dailyRate: data.dailyRate,
          monthlyRate: data.monthlyRate,
          tags: data.tags,
          files: data.files,
          links: data.links,
        },
        update: (cache, { data: { createCandidate } }) => {
          const newData = deepClone(
            cache.readQuery({
              query: AvailableCandidates,
              variables: {
                rateType: this.rateType,
                currency: this.currency,
              },
            }),
          )
          newData.me.activeAccount.candidates.data.push(createCandidate)

          cache.writeQuery({
            query: AvailableCandidates,
            variables: {
              rateType: this.rateType,
              currency: this.currency,
            },
            data: newData,
          })
        },
      }

      return this.$apollo
        .mutate(createRecruiterFreelancerMutation)
        .then((result) => {
          this.addCandidate(result.data.createCandidate)
          return result
        })
        .catch((error) => {
          throw error
        })
    },
    async syncCvFileTags() {
      const attachMutation = gql`
        mutation attachFileTags($file: FileIntOrIDInput!, $tags: [String]) {
          attachFileTags(file: $file, tags: $tags)
        }
      `
      const detachMutation = gql`
        mutation detachFileTags($file: FileIntOrIDInput!, $tags: [String]) {
          detachFileTags(file: $file, tags: $tags)
        }
      `

      const tagPromises = this.selectedCandidates.flatMap((candidate) => [
        ...(candidate.cvFiles ?? []).map((file) =>
          this.$apollo.mutate({
            mutation: attachMutation,
            variables: { file: { globalId: file.globalId }, tags: ['CV'] },
          }),
        ),
        ...(candidate.files ?? [])
          .filter((file) => file.tags?.some((tag) => tag.name === 'CV'))
          .map((file) =>
            this.$apollo.mutate({
              mutation: detachMutation,
              variables: { file: { globalId: file.globalId }, tags: ['CV'] },
            }),
          ),
      ])

      await Promise.all(tagPromises)
    },
    async createSupplierCandidateAndAdd(data) {
      const variables = {
        firstName: data.firstName,
        middleName: data.middleName,
        lastName: data.lastName,
        email: data.email,
        jobTitle: data.jobTitle,
        country: data.country,
        state: data.state,
        currency: this.currency,
        rateType: this.rateType,
        hourlyRate: data.hourlyRate,
        dailyRate: data.dailyRate,
        monthlyRate: data.monthlyRate,
        tags: data.tags,
        files: data.files,
        links: data.links,
      }

      return this.$apollo
        .mutate({
          mutation: CreateSupplierCandidateMutation,
          variables,
          update: (cache, { data: { createSupplierCandidate } }) => {
            const cacheQuery = {
              query: AvailableSupplierCandidates,
              variables: {
                rateType: this.rateType,
                currency: this.currency,
              },
            }
            const cachedData = cache.readQuery(cacheQuery)
            if (cachedData) {
              const newData = deepClone(cachedData)
              newData.me.activeAccount.supplierCandidates.data.push(createSupplierCandidate)
              cache.writeQuery({ ...cacheQuery, data: newData })
            }
          },
        })
        .then((result) => {
          this.addCandidate(result.data.createSupplierCandidate)
          return result
        })
        .catch((error) => {
          throw error
        })
    },
    getLinksErrors(formErrors, candidateIndex) {
      return Object.keys(formErrors)
        .filter((key) => key.startsWith(`input.candidates.${candidateIndex}.links.`))
        .reduce((accumulator, current) => {
          const newLinkKey = current.replace(`input.candidates.${candidateIndex}.`, '')
          accumulator[newLinkKey] = formErrors[current]
          return accumulator
        }, {})
    },
  },
}
</script>

<style scoped lang="scss">
@import 'resources/sass/frontend/_color-variables';

.add-candidate-button {
  margin-bottom: 8px;
}

.recruiter-pitch {
  margin-top: 28px;
}

.recruiter-pitch-footer {
  margin-top: 28px;

  .terms-banner {
    border-radius: 4px;
    padding: 12px;
    background-color: #f2f2f2;
    font-size: 12px;
    line-height: 16px;

    a {
      font-weight: 400;
      text-decoration: underline;
    }
  }
}

.info-icon-wrapper {
  border: 1px solid $gray-700;
  display: inline-flex;
  border-radius: 100%;
  width: 16px;
  height: 16px;
  justify-content: center;
  align-items: center;
  margin-right: 12px;
}

.recruiter-pitch-error {
  background-color: $red-50;
  border-radius: 4px;
  padding: 12px 14px;
}
</style>
